<template lang="pug">
.collection-setup
  .collection-setup__main
    .context-banner(v-if="orgName")
      span.context-banner__emoji 📦
      p.context-banner__text
        | {{ collectionId ? 'Collection for:' : 'Creating collection for:' }}
        |
        strong {{ orgName }}

    .details-card
      h2.section-title Collection Details
      div
        label.field-label(for="collection-title")
          | Title
          span.field-label__req  *
        input#collection-title.field-input(
          v-model="form.title"
          :class="{ 'is-invalid': titleError }"
          placeholder="Enter collection title"
          maxlength="120"
          @input="titleError = false; maybeAutoSlug()"
        )
      div
        label.field-label(for="collection-slug")
          | Slug
          span.field-label__req  *
        input#collection-slug.field-input.field-input--mono(
          :value="form.slug"
          placeholder="your-collection-slug"
          @input="onSlugInput"
        )
      div
        label.field-label(for="collection-description") Description (Optional)
        textarea#collection-description.field-input.field-input--textarea(
          v-model="form.description"
          rows="3"
          maxlength="2000"
          placeholder="Describe what collectors need to do to earn stickers"
        )
      div
        span.field-label Cover image (Optional)
        label.image-upload
          .image-upload__box
            img(v-if="form.coverImageUrl" :src="form.coverImageUrl" alt="Cover")
            fai(v-else type="fal" icon="plus").size-16
          div
            p.image-upload__title {{ coverUploading ? 'Uploading…' : 'Upload image' }}
            p.image-upload__hint PNG or JPG. Shown on the collector's Achievements tab
          input.d-none(
            type="file"
            accept="image/png,image/jpeg"
            :disabled="coverUploading"
            @change="onCoverSelected"
          )

    div
      .stickers-header
        h2.section-title Stickers ({{ sortedItems.length + drafts.length }})
        button.btn-primary(type="button" @click="addDraft")
          fai(type="fal" icon="plus").size-16
          | Add Sticker

      .stickers-empty(v-if="!sortedItems.length && !drafts.length")
        p.stickers-empty__emoji 🏷️
        p.stickers-empty__text No stickers yet. Add your first one above.
      .stickers-list(v-else)
        collection-sticker-item-card(
          v-for="(it, index) in sortedItems"
          :key="it.id"
          :ref="(el) => setItemRef(it.id, el)"
          :item="it"
          :collection-id="collectionId"
          :collection-owner-id="collection?.entityId || ''"
          :code-format="form.codeFormat"
          :can-move-up="index > 0"
          :can-move-down="index < sortedItems.length - 1"
          @refresh="$emit('refresh')"
          @move="onMoveItem"
        )
        collection-sticker-draft-card(
          v-for="(draft, index) in drafts"
          :key="draft.key"
          :draft="draft"
          :index="sortedItems.length + index"
          :org-id="orgId"
          :exclude-sticker-ids="existingStickerIds"
          :collection-owner-id="collection?.entityId || orgId"
          :code-format="form.codeFormat"
          :default-code-max-uses="parseMaxUses(form.defaultCodeMaxUses)"
          @update:draft="updateDraft"
          @remove="removeDraft(draft.key)"
        )

  aside.collection-setup__rail
    .rail-card
      h3.rail-card__title Collection Settings
      .rail-card__options
        label.rail-option
          input(v-model="form.requireAllStickersToComplete" type="checkbox")
          span Require all stickers to complete
        .rail-field(v-if="!form.requireAllStickersToComplete")
          label.field-label(for="collection-required-count") Stickers needed to complete
          input#collection-required-count.field-input(
            v-model="form.stickersRequiredToComplete"
            type="number"
            min="1"
            :max="MAX_REQUIRED_STICKERS"
            placeholder="e.g. 4"
          )
        label.rail-option
          input(v-model="form.showProgress" type="checkbox")
          span Show progress to collectors
        label.rail-option
          input(v-model="form.sendCompletionNotification" type="checkbox")
          span Send completion notification
        label.rail-option
          input(v-model="form.allowCodeReuse" type="checkbox")
          span Allow re-use of codes

    .rail-card
      h3.rail-card__title Code Options
      .rail-card__options
        .rail-field
          label.field-label(for="collection-code-format") Code format
          select#collection-code-format.field-input(v-model="form.codeFormat")
            option(v-for="f in codeFormats" :key="f.value" :value="f.value") {{ f.label }}
          p.rail-field__hint(v-if="form.codeFormat === 'CUSTOM'")
            | Codes are not generated. Type each code when you add it.
        .rail-field
          label.field-label(for="collection-default-max-uses") Default max uses
          input#collection-default-max-uses.field-input(
            v-model="form.defaultCodeMaxUses"
            type="number"
            min="1"
            placeholder="Unlimited"
          )
          p.rail-field__hint Applied to new codes added without a limit.

    .stats-card
      h3.stats-card__title Quick stats
      .stats-card__rows
        .stats-card__row
          span Stickers
          strong {{ sortedItems.length + drafts.length }}
        .stats-card__row
          span Total codes
          strong {{ totalCodes }}
        .stats-card__row
          span Collectors
          strong {{ collectorsCount }}
</template>

<script lang="ts">
import {
  computed, defineComponent, reactive, ref, watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import globalShared from '@/shared';
import fileUpload from '@/shared/upload';
import commonShared from '@/views/manage-users-and-organizations/shared';
import CollectionStickerItemCard from './CollectionStickerItemCard.vue';
import CollectionStickerDraftCard from './CollectionStickerDraftCard.vue';
import {
  CODE_FORMAT_OPTIONS,
  MAX_REQUIRED_STICKERS,
  ensureSlug,
  newDraftSticker,
  parseMaxUses,
  slugifyTitle,
  type DraftSticker,
} from '../shared';
import {
  mutateStickerCollection,
  mutateStickerCollectionItem,
  mutateStickerCollectionCode,
} from '@/graphs/gql';
import type {
  StickerCollectionCms,
  StickerCollectionCodeFormat,
} from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';

type ItemCardRef = { flush: () => Promise<boolean> };

export default defineComponent({
  name: 'CollectionSetupTab',
  components: { CollectionStickerItemCard, CollectionStickerDraftCard },
  props: {
    orgId: { type: String, required: true },
    orgName: { type: String, default: '' },
    collectionId: { type: String, default: '' },
    collection: { type: Object as () => StickerCollectionCms | null, default: null },
    collectorsCount: { type: Number, default: 0 },
  },
  emits: ['refresh', 'created'],
  setup(props, { emit }) {
    const { makeGraphRequest, getAuthToken } = globalShared();
    const { updateOrgSticker } = commonShared();
    const slugTouched = ref(false);
    const titleError = ref(false);
    const coverUploading = ref(false);
    const drafts = ref<DraftSticker[]>([]);
    const itemRefs = new Map<string, ItemCardRef>();
    const { uploadStatus, beforeUpload, handleUploadRequest } = fileUpload(
      'stickers',
      'Collections',
    );

    const form = reactive({
      title: '',
      slug: '',
      description: '',
      coverImageUrl: '',
      showProgress: true,
      allowCodeReuse: false,
      requireAllStickersToComplete: true,
      // Number inputs: kept as strings so an empty field means "not set".
      stickersRequiredToComplete: '' as string | number,
      sendCompletionNotification: false,
      codeFormat: 'ALPHANUMERIC' as StickerCollectionCodeFormat,
      defaultCodeMaxUses: '' as string | number,
    });

    // Fill the form once per collection. Item, code and note edits reload the collection; doing
    // it on every reload would wipe unsaved title/slug/description/settings changes.
    const hydratedId = ref('');
    watch(
      () => props.collection,
      (c) => {
        if (!c || c.id === hydratedId.value) return;
        hydratedId.value = c.id;
        form.title = c.title || '';
        form.slug = c.slug || '';
        form.description = c.description || '';
        form.coverImageUrl = c.coverImageUrl || '';
        form.showProgress = c.showProgress !== false;
        form.allowCodeReuse = !!c.allowCodeReuse;
        form.requireAllStickersToComplete = c.requireAllStickersToComplete !== false;
        form.stickersRequiredToComplete = c.requireAllStickersToComplete === false
          ? (c.stickersRequiredToComplete ?? '')
          : '';
        form.sendCompletionNotification = !!c.sendCompletionNotification;
        form.codeFormat = c.codeFormat || 'ALPHANUMERIC';
        form.defaultCodeMaxUses = c.defaultCodeMaxUses ?? '';
        if (c.slug) slugTouched.value = true;
      },
      { immediate: true },
    );

    const sortedItems = computed(() => {
      const items = props.collection?.items ?? [];
      return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
    });

    const existingStickerIds = computed(() => [
      ...sortedItems.value.map((i) => i.sticker?.id),
      ...drafts.value.map((d) => d.stickerId),
    ].filter(Boolean) as string[]);

    const totalCodes = computed(
      () => sortedItems.value.reduce((sum, it) => sum + (it.codes?.length ?? 0), 0)
        + drafts.value.reduce((sum, d) => sum + d.codes.length, 0),
    );

    function setItemRef(id: string, el: unknown) {
      if (el) itemRefs.set(id, el as ItemCardRef);
      else itemRefs.delete(id);
    }

    /** The slug follows the title while typing, until the slug is edited by hand. */
    function maybeAutoSlug() {
      if (slugTouched.value) return;
      form.slug = slugifyTitle(form.title);
    }

    function onSlugInput(e: Event) {
      const { value } = e.target as HTMLInputElement;
      // Slugs go into public URLs: only a-z, 0-9 and single hyphens.
      form.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      slugTouched.value = Boolean(form.slug);
    }

    function addDraft() {
      drafts.value.push(newDraftSticker());
    }

    function updateDraft(updated: DraftSticker) {
      drafts.value = drafts.value.map((d) => (d.key === updated.key ? updated : d));
    }

    function removeDraft(key: string) {
      drafts.value = drafts.value.filter((d) => d.key !== key);
    }

    async function onCoverSelected(e: Event) {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;
      if (!beforeUpload(file)) {
        ElMessage.error(uploadStatus.message || 'Invalid image');
        return;
      }
      coverUploading.value = true;
      try {
        await handleUploadRequest({ file });
        if (uploadStatus.status === 'ok') {
          form.coverImageUrl = uploadStatus.resourceUrl;
        } else {
          ElMessage.error(uploadStatus.message || 'Upload failed');
        }
      } finally {
        coverUploading.value = false;
      }
    }

    // Id of a collection created by this tab but not yet navigated to (see save()).
    const createdId = ref('');
    const activeCollectionId = computed(() => props.collectionId || createdId.value);

    async function saveMetadata(): Promise<string | null> {
      if (!form.title.trim()) {
        titleError.value = true;
        ElMessage.error('Title is required');
        return null;
      }
      let requiredCount: number | null = null;
      if (!form.requireAllStickersToComplete) {
        requiredCount = parseMaxUses(String(form.stickersRequiredToComplete));
        if (requiredCount == null) {
          ElMessage.error('Set how many stickers complete the collection, or require all of them.');
          return null;
        }
        if (requiredCount > MAX_REQUIRED_STICKERS) {
          ElMessage.error(
            `The number of stickers to complete the collection must be between 1 and ${
              MAX_REQUIRED_STICKERS}.`,
          );
          return null;
        }
      }
      form.slug = ensureSlug(form.slug || form.title, 'collection');
      const input: Record<string, unknown> = {
        title: form.title.trim(),
        slug: form.slug,
        // null means "leave unchanged" on edit, so a cleared description must be sent as "".
        description: form.description.trim(),
        coverImageUrl: form.coverImageUrl || null,
        showProgress: form.showProgress,
        allowCodeReuse: form.allowCodeReuse,
        requireAllStickersToComplete: form.requireAllStickersToComplete,
        sendCompletionNotification: form.sendCompletionNotification,
        codeFormat: form.codeFormat,
        // 0 resets the default to unlimited (null would leave it unchanged).
        defaultCodeMaxUses: parseMaxUses(String(form.defaultCodeMaxUses)) ?? 0,
      };
      if (requiredCount != null) input.stickersRequiredToComplete = requiredCount;
      if (activeCollectionId.value) {
        input.id = activeCollectionId.value;
      } else {
        input.orgId = props.orgId;
      }
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollection,
        { input },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to save collection');
        return null;
      }
      return response?.data?.mutateStickerCollection?.id ?? activeCollectionId.value ?? null;
    }

    function draftError(draft: DraftSticker, position: number): string | null {
      const label = `Sticker ${position}`;
      if (draft.mode === 'new') {
        if (!draft.name.trim()) return `${label}: enter a sticker name`;
        if (!draft.imageUrl) return `${label}: upload a sticker image`;
      } else if (!draft.stickerId) {
        return `${label}: choose a sticker`;
      }
      return null;
    }

    // Creates the sticker (for "Create new"), adds it to the collection, then adds its codes.
    // `added` tells the caller the sticker is already in the collection even when `error` is set
    // (a code failed), so the draft must not be kept and saved again.
    async function persistDraft(
      collectionId: string,
      draft: DraftSticker,
      sortOrder: number,
    ): Promise<{ error: string | null; added: boolean }> {
      let { stickerId } = draft;
      if (draft.mode === 'new' && draft.createdStickerId) {
        stickerId = draft.createdStickerId;
      } else if (draft.mode === 'new') {
        try {
          const created = await updateOrgSticker(props.orgId, {
            title: draft.name.trim(),
            slug: ensureSlug(draft.name, 'sticker'),
            url: draft.imageUrl,
            thumbnailUrl: draft.thumbnailUrl || draft.imageUrl,
            cost: 0,
          });
          stickerId = created?.id;
          if (stickerId) Object.assign(draft, { createdStickerId: stickerId });
        } catch (e) {
          return { error: (e as Error).message || 'Failed to create sticker', added: false };
        }
        if (!stickerId) return { error: 'Failed to create sticker', added: false };
      }
      const itemResponse = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionItem,
        {
          input: {
            collectionId,
            stickerId,
            sortOrder,
            clue: draft.clue.trim() || null,
          },
        },
        getAuthToken(),
      ));
      if (itemResponse?.errors?.length) {
        return { error: itemResponse.errors[0]?.message || 'Failed to add sticker', added: false };
      }
      const itemId = itemResponse?.data?.mutateStickerCollectionItem?.id;
      // A published collection rejects requiresCode on an item without an active code, so the
      // item is added first, then its codes, then the requirement is switched on.
      const codeResults = await Promise.all(draft.codes.map(async (code) => {
        const input: Record<string, unknown> = { itemId, code: code.code };
        if (code.maxUses != null) input.maxUses = code.maxUses;
        const codeResponse = await withGraphErrors(makeGraphRequest(
          'mutation',
          mutateStickerCollectionCode,
          { input },
          getAuthToken(),
        ));
        if (codeResponse?.errors?.length) {
          return `code ${code.code}: ${codeResponse.errors[0]?.message || 'Failed to add code'}`;
        }
        // An omitted maxUses takes the collection default; set it back to unlimited if wanted.
        const saved = codeResponse?.data?.mutateStickerCollectionCode;
        if (code.maxUses == null && saved?.maxUses != null) {
          const editResponse = await withGraphErrors(makeGraphRequest(
            'mutation',
            mutateStickerCollectionCode,
            { input: { id: saved.id, maxUses: null, isActive: saved.isActive } },
            getAuthToken(),
          ));
          if (editResponse?.errors?.length) {
            return `code ${code.code}: ${editResponse.errors[0]?.message || 'Failed to update code'}`;
          }
        }
        return null;
      }));
      let error = codeResults.find(Boolean) ?? null;
      if (draft.requiresCode) {
        const requireResponse = await withGraphErrors(makeGraphRequest(
          'mutation',
          mutateStickerCollectionItem,
          { input: { id: itemId, collectionId, requiresCode: true } },
          getAuthToken(),
        ));
        if (requireResponse?.errors?.length) {
          error = error ?? (requireResponse.errors[0]?.message || 'Failed to require a code');
        }
      }
      return { error, added: true };
    }

    // Saves details, pending item edits and new stickers. Returns true when everything saved.
    async function save(): Promise<boolean> {
      const firstDraftError = drafts.value
        .map((d, i) => draftError(d, sortedItems.value.length + i + 1))
        .find(Boolean);
      if (firstDraftError) {
        ElMessage.error(firstDraftError);
        return false;
      }

      const savedId = await saveMetadata();
      if (!savedId) return false;
      createdId.value = savedId;

      const itemResults = await Promise.all([...itemRefs.values()].map((r) => r.flush()));
      let ok = itemResults.every(Boolean);

      // One at a time so each new sticker lands at the end in the order shown.
      const remaining: DraftSticker[] = [];
      let sortOrder = sortedItems.value.length;
      await drafts.value.reduce(async (previous, draft) => {
        await previous;
        const { error, added } = await persistDraft(savedId, draft, sortOrder);
        if (added) sortOrder += 1;
        if (error) {
          ok = false;
          if (!added) remaining.push(draft);
          ElMessage.error(
            added ? `Sticker added, but ${error}` : error,
          );
        }
      }, Promise.resolve());
      drafts.value = remaining;

      if (ok) ElMessage.success('Collection saved');
      if (!props.collectionId) {
        // Navigating to the edit route remounts this tab and would drop drafts that failed to
        // save; stay put until they're all in (the id is kept in `createdId` for retries).
        if (drafts.value.length === 0) emit('created', savedId);
      } else {
        emit('refresh');
      }
      return ok;
    }

    async function onMoveItem({ itemId, delta }: { itemId: string; delta: number }) {
      const items = sortedItems.value;
      const index = items.findIndex((i) => i.id === itemId);
      if (index < 0) return;
      const targetIndex = index + delta;
      if (targetIndex < 0 || targetIndex >= items.length) return;
      // Backend renumbers the other items, so one move is enough.
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionItem,
        { input: { id: items[index].id, sortOrder: targetIndex } },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to move sticker');
      }
      emit('refresh');
    }

    return {
      form,
      codeFormats: CODE_FORMAT_OPTIONS,
      MAX_REQUIRED_STICKERS,
      parseMaxUses,
      titleError,
      coverUploading,
      drafts,
      sortedItems,
      existingStickerIds,
      totalCodes,
      setItemRef,
      maybeAutoSlug,
      onSlugInput,
      addDraft,
      updateDraft,
      removeDraft,
      onCoverSelected,
      save,
      onMoveItem,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../collection-cms';

// flex gap-6 max-w-6xl
.collection-setup {
  display: flex;
  gap: 24px;
  max-width: 1152px;
  align-items: flex-start;
}

.collection-setup__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3
.context-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: $indigo-50;
  border: 1px solid $indigo-100;
  border-radius: 12px;
  padding: 12px 16px;

  &__emoji {
    font-size: 20px;
    line-height: 28px;
  }

  &__text {
    @include text-sm;
    color: $indigo-700;
    margin: 0;

    strong {
      font-weight: 700;
    }
  }
}

.details-card {
  @include card;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  @include section-title;
}

.field-label {
  @include field-label;

  &__req {
    color: $red-400;
  }
}

.field-input {
  @include input;

  &--mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  &--textarea {
    resize: none;
  }

  &.is-invalid {
    border-color: $red-400;
  }
}

.image-upload {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  &__box {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    border-radius: 12px;
    border: 2px dashed $gray-200;
    background: $gray-50;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: $gray-300;
    transition: border-color 0.15s;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &:hover &__box {
    border-color: $teal-light;
  }

  &__title {
    @include text-sm;
    color: $gray-600;
    margin: 0;
  }

  &:hover &__title {
    color: $gray-800;
  }

  &__hint {
    @include text-xs;
    color: $gray-400;
    margin: 2px 0 0;
  }
}

.stickers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.btn-primary {
  @include btn-primary;
}

// bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center
.stickers-empty {
  background: white;
  border: 1px dashed $gray-300;
  border-radius: 12px;
  padding: 40px;
  text-align: center;

  &__emoji {
    font-size: 30px;
    line-height: 36px;
    margin: 0 0 8px;
  }

  &__text {
    @include text-sm;
    color: $gray-500;
    margin: 0;
  }
}

.stickers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// w-64 flex-shrink-0 space-y-4
.collection-setup__rail {
  width: 256px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rail-card {
  @include card;
  padding: 16px;

  &__title {
    @include text-sm;
    font-weight: 600;
    color: $gray-700;
    margin: 0 0 12px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

.rail-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  margin: 0;

  input {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    accent-color: $teal;
    cursor: pointer;
    flex-shrink: 0;
  }

  span {
    @include text-sm;
    line-height: 1.375;
    color: $gray-600;
    transition: color 0.15s;
  }

  &:hover span {
    color: $gray-800;
  }
}

.rail-field {
  display: flex;
  flex-direction: column;

  .field-input {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  &__hint {
    @include text-xs;
    color: $gray-400;
    margin: 6px 0 0;
  }
}

// bg-teal-50 rounded-xl border border-teal-100 p-4
.stats-card {
  background: $teal-50;
  border: 1px solid $teal-100;
  border-radius: 12px;
  padding: 16px;

  &__title {
    @include text-sm;
    font-weight: 600;
    color: $teal;
    margin: 0 0 4px;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    @include text-sm;
    color: $teal-700;

    strong {
      font-weight: 600;
    }
  }
}
</style>
